import {
  DiscoveryApi,
  FetchApi,
  createApiRef,
} from '@backstage/core-plugin-api';
import {
  CompositionRow,
  DependencyGraph,
  DependencyRow,
  RepoFreshness,
  Summary,
  TrendPoint,
} from './types';

export interface DependencyMapApi {
  getSummary(): Promise<Summary>;
  getComposition(): Promise<CompositionRow[]>;
  getFreshness(): Promise<RepoFreshness[]>;
  getGraph(language: string, minRepos: number): Promise<DependencyGraph>;
  getDependencies(): Promise<DependencyRow[]>;
  getTrend(): Promise<TrendPoint[]>;
  refresh(): Promise<{ updated: boolean }>;
}

export const dependencyMapApiRef = createApiRef<DependencyMapApi>({
  id: 'plugin.dependency-map.service',
});

export class DependencyMapClient implements DependencyMapApi {
  constructor(
    private readonly options: {
      discoveryApi: DiscoveryApi;
      fetchApi: FetchApi;
    },
  ) {}

  private async request<T>(path: string, init?: RequestInit): Promise<T> {
    const baseUrl =
      await this.options.discoveryApi.getBaseUrl('dependency-map');
    const response = await this.options.fetchApi.fetch(
      `${baseUrl}${path}`,
      init,
    );
    if (!response.ok) {
      throw new Error(
        `Dependency map request ${path} failed: ${response.status}`,
      );
    }
    return (await response.json()) as T;
  }

  getSummary() {
    return this.request<Summary>('/summary');
  }

  getComposition() {
    return this.request<CompositionRow[]>('/composition');
  }

  getFreshness() {
    return this.request<RepoFreshness[]>('/freshness');
  }

  getGraph(language: string, minRepos: number) {
    const query = new URLSearchParams({
      language,
      minRepos: String(minRepos),
    });
    return this.request<DependencyGraph>(`/graph?${query}`);
  }

  getDependencies() {
    return this.request<DependencyRow[]>('/dependencies');
  }

  getTrend() {
    return this.request<TrendPoint[]>('/trend');
  }

  refresh() {
    return this.request<{ updated: boolean }>('/refresh', { method: 'POST' });
  }
}
